import TuneIcon from '@mui/icons-material/Tune';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import type { ActionArgs, LoaderArgs, MetaFunction } from '@remix-run/node';
import { json, redirect } from '@remix-run/node';
import { useLoaderData, useSearchParams } from '@remix-run/react';
import { useState } from 'react';
import { addToDesignLab } from '~/components/DesignLab/utils.server';
import { getLdFlag } from '~/components/launchdarkly.server';
import DrawerWithCloseIcon from '~/components/shared/DrawerWithCloseIcon';
import { MuiRemixLink } from '~/components/shared/links';
import ProductCard from '~/components/shared/ProductCard';
import Shop from '~/components/Shop';
import {
  buildQueryWithTypes,
  EmptyState,
  PackFilter,
  PackTypeToggle,
  PriceRangeFilter,
  ProductionTimeFilter,
} from '~/components/Shop/common';
import ProductsGridWithFilters from '~/components/Shop/ProductsGridWithFilters';
import { SearchText } from '~/components/Shop/SearchText';
import ShopLanding from '~/components/Shop/ShopLanding';
import type { ConfigurationType, ProductsResponse } from '~/services/api.server';
import { cartApi, scApi } from '~/services/api.server';
import { getUpdatedAuthentication } from '~/services/auth.server';
import { commitSession, getSession } from '~/services/session.server';
import { ConfigurationTypes, ProductTypes } from '~/utils/constants';
import { TYPES_PARAM } from '~/utils/searchParams';
import {
  getBulkProductsWithLogo,
  getCategories,
  getCategoryNode,
  getProducts,
  RemoveUnsetSearchParams,
} from '~/utils/shop';
import type { SitemapHandle } from '~/utils/sitemap.server';

export const handle: SitemapHandle = {
  getSitemapEntries: (path) => ({
    route: path,
    lastmod: new Date().toISOString(),
    changefreq: 'daily',
    priority: 0.8,
  }),
};

export const meta: MetaFunction = ({ data }) => {
  const { selectedCategory } = data;

  return {
    title: 'SwagCharm | Shop',
    description: selectedCategory?.description,
    keywords: selectedCategory?.keywords,
  };
};

export async function loader({ request }: LoaderArgs) {
  const url = new URL(request.url);
  RemoveUnsetSearchParams(url);

  const [{ user }, categories] = await Promise.all([
    getUpdatedAuthentication(request),
    getCategories(url.searchParams),
  ]);
  const session = await getSession(request);
  const accountId = session.get('accountId');

  const productsFilters = await scApi.getProductsAvailableFilters(user?.accessToken, accountId);

  const categoriesList = categories.map((c) => {
    const node = getCategoryNode(c, c.id !== 1 ? '/shop/bulk-swag' : '/shop');

    return [node];
  });

  const breadcrumbEntries = [
    { name: 'Home', href: '/' },
    { name: 'Shop', href: '/shop' },
  ];

  const types = url.searchParams.getAll(TYPES_PARAM);
  if (types.length === 1) {
    switch (types[0]) {
      case ProductTypes.CUSTOM_PACK.toString():
        url.searchParams.append(TYPES_PARAM, ProductTypes.CUSTOM_PRODUCT.toString());
        break;
      case ProductTypes.PACK_TEMPLATE.toString():
        url.searchParams.append(TYPES_PARAM, ProductTypes.SUPPLIER_PRODUCT.toString());
        break;
      case ProductTypes.CUSTOM_PRODUCT.toString():
        url.searchParams.append(TYPES_PARAM, ProductTypes.CUSTOM_PACK.toString());
        break;
      case ProductTypes.SUPPLIER_PRODUCT.toString():
        url.searchParams.append(TYPES_PARAM, ProductTypes.PACK_TEMPLATE.toString());
        break;
      default:
        url.searchParams.delete(TYPES_PARAM);
    }
    throw redirect(url.toString());
  }

  const isPackType = (type: string) =>
    type === ProductTypes.CUSTOM_PACK.toString() ||
    type === ProductTypes.PACK_TEMPLATE.toString() ||
    type === ProductTypes.CUSTOM_PACK_TEMPLATE.toString();

  const getProductsURL = (productTypes: number[], filterFn: (t: string) => boolean) => {
    const productsUrl = new URL(url);
    if (types.length === 0) {
      productTypes.forEach((t) => productsUrl.searchParams.append(TYPES_PARAM, t.toString()));
    } else {
      productsUrl.searchParams.delete(TYPES_PARAM);
      types.filter(filterFn).forEach((t) => productsUrl.searchParams.append(TYPES_PARAM, t));
    }

    return productsUrl;
  };

  const packProductsUrl = getProductsURL(
    [ProductTypes.PACK_TEMPLATE, ProductTypes.CUSTOM_PACK, ProductTypes.CUSTOM_PACK_TEMPLATE],
    isPackType
  );
  const bulkProductsUrl = getProductsURL(
    [ProductTypes.SUPPLIER_PRODUCT, ProductTypes.CUSTOM_PRODUCT],
    (t) => !isPackType(t)
  );

  const showAutomockupsFlag = await getLdFlag(user, 'show-auto-mockups-temp-05042023');

  const [packProducts, bulkProducts] = await Promise.all([
    getProducts(packProductsUrl.searchParams, user?.accessToken, accountId, productsFilters),
    getBulkProductsWithLogo(
      showAutomockupsFlag,
      bulkProductsUrl.searchParams,
      user?.accessToken,
      accountId,
      productsFilters
    ),
  ]);

  return json({
    isAuthenticated: user !== null,
    packProducts,
    bulkProducts,
    productsFilters,
    categories: categoriesList,
    breadcrumbEntries,
  });
}

export async function action({ request }: ActionArgs) {
  const [{ headers = new Headers() }, formData, session] = await Promise.all([
    getUpdatedAuthentication(request),
    request.formData(),
    getSession(request),
  ]);
  const formEntries = Object.fromEntries(formData);
  const product = JSON.parse(formEntries.product.toString());
  const action = formEntries._action;

  if (action === 'addDesignLab') {
    await addToDesignLab(session, formData, headers, product);
  }

  const quantity = Number(formEntries.quantity) || 1;
  const packAttributes = JSON.parse(formEntries.packAttributes?.toString() || 'null');
  const configuration_type = (formEntries.configuration_type as ConfigurationType) || ConfigurationTypes.decorated;

  if (action === 'addToCart') {
    cartApi.addProduct(session, quantity, product, packAttributes, configuration_type);
    headers.append('Set-Cookie', await commitSession(session));
  }

  return json({ cart: cartApi.getCart(session) }, { headers });
}

export default function Products() {
  const { packProducts, bulkProducts, ...data } = useLoaderData<typeof loader>();
  const [query] = useSearchParams();
  const selectedFiltersCount = [...query].length;

  const [filtersOpen, setFiltersOpen] = useState(false);

  function toggleFiltersOpen() {
    setFiltersOpen((prev) => !prev);
  }

  const onlyPacks = packProducts.count > 0 && bulkProducts.count === 0;
  const onlyBulks = bulkProducts.count > 0 && packProducts.count == 0;
  const isEmpty = bulkProducts.count == 0 && packProducts.count == 0;

  if (selectedFiltersCount === 0) return <ShopLanding />;

  return (
    <Shop {...data} products={packProducts.count > 0 ? packProducts : bulkProducts}>
      {onlyPacks || onlyBulks ? (
        <ProductsGridWithFilters
          categories={data.categories}
          productsFilters={data.productsFilters}
          productGridProps={{
            products: onlyPacks ? packProducts : bulkProducts,
            renderCarousel: true,
          }}
        />
      ) : (
        <Stack>
          <SearchText sx={{ mb: 1.5 }} />
          <Box sx={{ mb: 1.5, display: { xs: 'block', sm: 'none' } }}>
            <Button variant="text" onClick={toggleFiltersOpen} startIcon={<TuneIcon />}>
              Filters {selectedFiltersCount > 0 ? `(${selectedFiltersCount})` : null}
            </Button>
            <DrawerWithCloseIcon open={filtersOpen} onClose={toggleFiltersOpen}>
              <Stack py={1} px={2}>
                {data.isAuthenticated && <PackTypeToggle />}
                <PackFilter categories={data.categories} onNavigate={toggleFiltersOpen} clearSearch={!isEmpty} />
                <PriceRangeFilter minMax={data.productsFilters.price} />
                <ProductionTimeFilter minMax={data.productsFilters.production_time} />
              </Stack>
            </DrawerWithCloseIcon>
          </Box>
          {isEmpty ? (
            <EmptyState />
          ) : (
            <Stack gap={4}>
              <ProductsPreview
                title="Swag Packs"
                products={packProducts}
                viewMoreHref={`swag-packs?${buildQueryWithTypes('swag-packs', query)}`}
              />
              <ProductsPreview
                title="Bulk Swag"
                products={bulkProducts}
                viewMoreHref={`bulk-swag?${buildQueryWithTypes('bulk-swag', query)}`}
              />
            </Stack>
          )}
        </Stack>
      )}
    </Shop>
  );
}

function ProductsPreview({
  title,
  products,
  viewMoreHref,
}: {
  title: string;
  products: ProductsResponse;
  viewMoreHref: string;
}) {
  return (
    <Stack pb={2.5} bgcolor="#F0F5F9" borderRadius={2} sx={{ p: { xs: 1.5, sm: 3 } }}>
      <Stack direction="row" mb={2} alignItems="center">
        <Typography
          variant="subtitle2"
          sx={{
            mr: 2,
            pr: 0.5,
            borderBottom: (theme) => `2px solid ${theme.palette.primary.main}`,
          }}
        >
          {title}
        </Typography>
      </Stack>
      <Box
        display="grid"
        mb={4}
        sx={{
          gap: { xs: 1, sm: 2, lg: 3.25 },
          gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
        }}
      >
        {products.results.slice(0, 3).map((p) => (
          <ProductCard key={p.id} product={p} />
        ))}
      </Box>
      {products.count > 3 && (
        <Box display="grid" sx={{ placeItems: 'center' }}>
          <Button
            component={MuiRemixLink}
            href={`/shop/${viewMoreHref}`}
            prefetch="intent"
            size="small"
            disableRipple
            disableFocusRipple
            disableTouchRipple
            sx={{ textTransform: 'uppercase', bgcolor: 'transparent !important' }}
          >
            View More &nbsp;<span style={{ textTransform: 'none' }}>({products.count} items)</span>
          </Button>
        </Box>
      )}
    </Stack>
  );
}
